import { useState, useCallback } from 'react'
import { PageHeader, DataTable, ColumnSettingsButton, colors, input, btnSecondary } from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import type { CentralAuditLogRow, Environment } from '@alpha-edge/shared'
import { useTableColumns } from '../../hooks/use-table-columns'

// ── styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
}

const chipStyle = (selected: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  borderRadius: '9999px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  cursor: 'pointer',
  border: selected ? `2px solid ${colors.primary}` : `2px solid ${colors.border}`,
  background: selected ? '#eff6ff' : '#fff',
  color: selected ? colors.primary : colors.text,
  userSelect: 'none',
  transition: 'all 0.12s',
})

const chipRemoveStyle: React.CSSProperties = {
  marginLeft: '6px',
  fontSize: '0.75rem',
  cursor: 'pointer',
  color: colors.textMuted,
  lineHeight: 1,
}

const filterRowStyle: React.CSSProperties = {
  marginBottom: '16px',
}

// ── helpers ───────────────────────────────────────────────────────────────────

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ts
  }
}

function truncateJson(val: Record<string, unknown> | null): string {
  if (!val) return '—'
  const str = JSON.stringify(val)
  return str.length > 80 ? str.slice(0, 77) + '...' : str
}

// ── action + entity type options ──────────────────────────────────────────────

const ACTIONS = ['', 'create', 'update', 'delete', 'login', 'logout', 'read'] as const
type AuditAction = (typeof ACTIONS)[number]

// ── columns ───────────────────────────────────────────────────────────────────

const columns: readonly Column<CentralAuditLogRow>[] = [
  {
    key: 'ingestedAt',
    header: 'Ingested',
    render: (r) => formatTimestamp(r.ingestedAt),
    sortable: true,
  },
  {
    key: 'sourceEnvironmentName',
    header: 'Environment',
    render: (r) => r.sourceEnvironmentName ?? r.sourceEnvironmentId,
    sortable: true,
  },
  {
    key: 'entityType',
    header: 'Entity Type',
    render: (r) => r.entityType,
    sortable: true,
    filterable: true,
  },
  {
    key: 'action',
    header: 'Action',
    render: (r) => r.action,
    sortable: true,
  },
  {
    key: 'entityId',
    header: 'Entity ID',
    render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.entityId}</span>,
  },
  {
    key: 'changedByName',
    header: 'Changed By',
    render: (r) => r.changedByName ?? r.changedById ?? '—',
    sortable: true,
  },
  {
    key: 'oldValues',
    header: 'Old Values',
    render: (r) => (
      <span title={r.oldValues ? JSON.stringify(r.oldValues, null, 2) : undefined}>{truncateJson(r.oldValues)}</span>
    ),
  },
  {
    key: 'newValues',
    header: 'New Values',
    render: (r) => (
      <span title={r.newValues ? JSON.stringify(r.newValues, null, 2) : undefined}>{truncateJson(r.newValues)}</span>
    ),
  },
]

// ── component ─────────────────────────────────────────────────────────────────

export function FleetAuditPage() {
  const [selectedEnvIds, setSelectedEnvIds] = useState<readonly string[]>([])
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState<AuditAction>('')
  const [adminId, setAdminId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const { tableId: _, ...tableColumnProps } = useTableColumns('fleet-audit')

  // Build filter object for useApiList — omit undefined so params aren't sent.
  const filters: Record<string, string | undefined> = {
    ...(selectedEnvIds.length > 0
      ? Object.fromEntries(selectedEnvIds.map((id, i) => [`environmentId[${i}]`, id]))
      : {}),
    entityType: entityType || undefined,
    action: action || undefined,
    adminId: adminId || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
  }

  const {
    data: rows,
    total,
    loading,
    error,
  } = useApiList<CentralAuditLogRow>({
    path: '/admin/fleet/audit',
    limit: 100,
    filters,
  })

  const { data: environments, loading: envsLoading } = useApiList<Environment>({
    path: '/fleet/environments',
  })

  const toggleEnv = useCallback((id: string) => {
    setSelectedEnvIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }, [])

  const hasFilters = selectedEnvIds.length > 0 || entityType || action || adminId || from || to

  const clearFilters = useCallback(() => {
    setSelectedEnvIds([])
    setEntityType('')
    setAction('' as AuditAction)
    setAdminId('')
    setFrom('')
    setTo('')
  }, [])

  const selectedEnvs = environments.filter((e) => selectedEnvIds.includes(e.id))
  const unselectedEnvs = environments.filter((e) => !selectedEnvIds.includes(e.id))

  return (
    <div>
      <PageHeader
        title="Fleet Audit Log"
        subtitle={`Cross-environment audit log for all enrolled deployments${total > 0 ? ` — ${total.toLocaleString()} rows` : ''}`}
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      <div style={{ padding: '24px' }}>
        {/* Environment filter chips */}
        <div style={filterRowStyle}>
          <label style={labelStyle}>Filter by Environment (all environments if none selected)</label>
          {selectedEnvs.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {selectedEnvs.map((env) => (
                <span key={env.id} style={chipStyle(true)}>
                  {env.name}
                  <span
                    style={chipRemoveStyle}
                    role="button"
                    onClick={() => toggleEnv(env.id)}
                    title={`Remove ${env.name}`}
                  >
                    x
                  </span>
                </span>
              ))}
            </div>
          )}
          {envsLoading ? (
            <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>Loading environments...</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {unselectedEnvs.map((env) => (
                <span
                  key={env.id}
                  style={chipStyle(false)}
                  role="button"
                  onClick={() => toggleEnv(env.id)}
                  title={`Add ${env.name}`}
                >
                  {env.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Entity type + action + adminId filters */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Entity Type</label>
            <input
              type="text"
              placeholder="e.g. patient"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              style={{ ...input, width: '160px' }}
            />
          </div>

          <div>
            <label style={labelStyle}>Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as AuditAction)}
              style={{ ...input, width: '140px' }}
            >
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a || '(all)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Admin ID / Name</label>
            <input
              type="text"
              placeholder="user id or name"
              value={adminId}
              onChange={(e) => setAdminId(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>

        {/* Date range */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '16px' }}>
          <div>
            <label style={labelStyle}>From (ingest time)</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
          <div>
            <label style={labelStyle}>To (ingest time)</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>

        {/* Clear filters */}
        {hasFilters && (
          <div style={{ marginBottom: '16px' }}>
            <button style={btnSecondary} onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="id"
            emptyMessage="No audit rows found for the selected filters."
            {...tableColumnProps}
          />
        )}
      </div>
    </div>
  )
}
